/* Scroll-snap carousels: [data-carousel] wraps a .carousel__track (a <ul>
   that scrolls horizontally) and two .carousel__arrow buttons. Touch,
   trackpad and keyboard scrolling are native; this script steps the track
   one slide per arrow press, keeps the arrows' disabled state in sync with
   the scroll position, and lets a mouse drag the track like a swipe. */
(() => {
  'use strict';

  // Each carousel section includes this file, so it can be executed twice
  if (window.psjCarouselsLoaded) return;
  window.psjCarouselsLoaded = true;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initCarousel = (root) => {
    if (root.dataset.carouselReady) return;
    const track = root.querySelector('.carousel__track');
    const prev = root.querySelector('.carousel__arrow--prev');
    const next = root.querySelector('.carousel__arrow--next');
    if (!track) return;
    root.dataset.carouselReady = 'true';

    const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');

    // One slide plus the gap between slides
    const step = () => {
      const slide = track.querySelector('.carousel__slide');
      if (!slide) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return slide.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const atStart = track.scrollLeft <= 1;
      const atEnd = track.scrollLeft >= max - 1;
      root.classList.toggle('is-static', max <= 1);
      prev?.setAttribute('aria-disabled', String(atStart));
      next?.setAttribute('aria-disabled', String(atEnd));
    };

    let frame = 0;
    const scheduleUpdate = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    // aria-disabled rather than disabled, so focus stays on the arrow at the ends
    const move = (direction) => (event) => {
      if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;
      track.scrollBy({ left: direction * step(), behavior: behavior() });
    };

    prev?.addEventListener('click', move(-1));
    next?.addEventListener('click', move(1));
    track.addEventListener('scroll', scheduleUpdate, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(scheduleUpdate).observe(track);

    // Mouse drag. Touch and pens already scroll natively.
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragged = false;
    let suppressClick = false;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragStartX = event.clientX;
      dragStartScroll = track.scrollLeft;
      dragged = false;

      const onMove = (moveEvent) => {
        const dx = moveEvent.clientX - dragStartX;
        if (!dragged && Math.abs(dx) < 6) return;
        if (!dragged) {
          dragged = true;
          track.classList.add('is-dragging');
        }
        track.scrollLeft = dragStartScroll - dx;
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!dragged) return;
        // The click that ends a drag fires straight after pointerup; ignore just that one
        suppressClick = true;
        setTimeout(() => (suppressClick = false), 0);
        // Re-enable snapping from where the drag left off, then settle on a slide
        const left = track.scrollLeft;
        track.classList.remove('is-dragging');
        track.scrollLeft = left;
        const settled = Math.round(left / step()) * step();
        track.scrollTo({ left: settled, behavior: behavior() });
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    });

    // A drag ends with a click on whatever link was under the pointer: swallow it
    track.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    // Stop the browser's native image/link drag from hijacking the gesture
    track.addEventListener('dragstart', (event) => event.preventDefault());

    update();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-carousel]').forEach(initCarousel);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor: re-init a re-rendered section, and bring a selected block into view
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('.carousel__slide');
    const track = slide?.closest('.carousel__track');
    if (!slide || !track) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'auto' });
  });
})();
